import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  BarChart3,
  RefreshCw,
  Loader2,
  LayoutDashboard,
  Download,
  Plus,
  FolderPlus,
  ChevronDown,
} from 'lucide-react';
import {
  barrattAPI,
  type AnalyseCategory,
  type AnalyseDashboard,
  type DashboardIndicator,
} from '../../lib/api/barratt';
import { toast } from 'sonner';
import { IndicatorCard } from './AnalyseCharts';
import {
  CreateCategoryDialog,
  CreateDashboardDialog,
  IndicatorDialog,
  DeleteIndicatorDialog,
} from './AnalyseDialogs';

export default function Analyse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardId = searchParams.get('dashboard');

  const [categories, setCategories] = useState<AnalyseCategory[]>([]);
  const [dashboards, setDashboards] = useState<AnalyseDashboard[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<AnalyseDashboard | null>(null);
  const [indicators, setIndicators] = useState<DashboardIndicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingIndicators, setLoadingIndicators] = useState(false);

  // Dialog states
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isAddDashboardOpen, setIsAddDashboardOpen] = useState(false);
  const [isAddIndicatorOpen, setIsAddIndicatorOpen] = useState(false);
  const [isEditIndicatorOpen, setIsEditIndicatorOpen] = useState(false);
  const [isDeleteIndicatorOpen, setIsDeleteIndicatorOpen] = useState(false);

  // Form states
  const [newCategory, setNewCategory] = useState({ name: '', hint: '', sort_order: 0 });
  const [newDashboard, setNewDashboard] = useState({
    name: '', description: '', category: 0, is_enabled: true,
    shared_with: 'company' as 'private' | 'company' | 'company_and_suppliers'
  });
  const [newIndicator, setNewIndicator] = useState({
    name: '', chart_type: 'bar' as DashboardIndicator['chart_type'], hint: '',
    config: {} as Record<string, unknown>
  });
  const [editingIndicator, setEditingIndicator] = useState<DashboardIndicator | null>(null);
  const [deletingIndicator, setDeletingIndicator] = useState<DashboardIndicator | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [savingIndicator, setSavingIndicator] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesRes, dashboardsRes] = await Promise.all([
          barrattAPI.getCategories(),
          barrattAPI.getDashboards(),
        ]);
        setCategories(categoriesRes.data.results || []);
        setDashboards(dashboardsRes.data.results || []);
        if (dashboardId) {
          const dashboard = (dashboardsRes.data.results || []).find((d) => d.id === parseInt(dashboardId));
          if (dashboard) {
            setSelectedDashboard(dashboard);
            fetchIndicators(dashboard.id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dashboardId]);

  const fetchIndicators = async (dashboardIdToFetch: number) => {
    setLoadingIndicators(true);
    try {
      const response = await barrattAPI.getIndicators({ category_dashboard: dashboardIdToFetch });
      setIndicators(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch indicators:', error);
      toast.error('Failed to load indicators');
    } finally {
      setLoadingIndicators(false);
    }
  };

  const handleDashboardChange = (dashboardIdStr: string) => {
    const id = parseInt(dashboardIdStr);
    const dashboard = dashboards.find((d) => d.id === id);
    if (dashboard) {
      setSelectedDashboard(dashboard);
      setSearchParams({ dashboard: dashboardIdStr });
      fetchIndicators(id);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const [categoriesRes, dashboardsRes] = await Promise.all([
        barrattAPI.getCategories(), barrattAPI.getDashboards(),
      ]);
      setCategories(categoriesRes.data.results || []);
      setDashboards(dashboardsRes.data.results || []);
      if (selectedDashboard) fetchIndicators(selectedDashboard.id);
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) return;
    setSavingCategory(true);
    try {
      const response = await barrattAPI.createCategory({
        name: newCategory.name.trim(), hint: newCategory.hint.trim() || undefined,
        sort_order: newCategory.sort_order || categories.length + 1,
      });
      setCategories([...categories, response.data]);
      setNewCategory({ name: '', hint: '', sort_order: 0 });
      setIsAddCategoryOpen(false);
      toast.success('Category created');
    } catch (error) { console.error('Failed to create category:', error); toast.error('Failed to create category'); }
    finally { setSavingCategory(false); }
  };

  const handleCreateDashboard = async () => {
    if (!newDashboard.name.trim() || !newDashboard.category) return;
    setSavingDashboard(true);
    try {
      const response = await barrattAPI.createDashboard({
        name: newDashboard.name.trim(), description: newDashboard.description.trim() || undefined,
        category: newDashboard.category, is_enabled: newDashboard.is_enabled, shared_with: newDashboard.shared_with,
      });
      setDashboards([...dashboards, response.data]);
      setSelectedDashboard(response.data);
      setSearchParams({ dashboard: response.data.id.toString() });
      setNewDashboard({ name: '', description: '', category: 0, is_enabled: true, shared_with: 'company' });
      setIsAddDashboardOpen(false);
      setIndicators([]);
      toast.success('Dashboard created');
    } catch (error) { console.error('Failed to create dashboard:', error); toast.error('Failed to create dashboard'); }
    finally { setSavingDashboard(false); }
  };

  const handleCreateIndicator = async () => {
    if (!newIndicator.name.trim() || !selectedDashboard) return;
    setSavingIndicator(true);
    try {
      const response = await barrattAPI.createIndicator({
        name: newIndicator.name.trim(), chart_type: newIndicator.chart_type,
        hint: newIndicator.hint.trim() || undefined, category: selectedDashboard.category,
        config: newIndicator.config,
      });
      setIndicators([...indicators, response.data]);
      setNewIndicator({ name: '', chart_type: 'bar', hint: '', config: {} });
      setIsAddIndicatorOpen(false);
      toast.success('Indicator created');
    } catch (error: unknown) {
      console.error('Failed to create indicator:', error);
      // Extract error message from API response
      let errorMessage = 'Failed to create indicator 123';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: Record<string, unknown> } };
        const data = axiosError.response?.data;
        if (data) {
          // Handle field-specific errors (e.g., { company: ['error message'] })
          const fieldErrors = Object.entries(data)
            .filter(([key]) => key !== 'status' && key !== 'code')
            .map(([, value]) => (Array.isArray(value) ? value.join(', ') : String(value)))
            .join('. ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }
      toast.error(errorMessage);
    }
    finally { setSavingIndicator(false); }
  };

  const handleEditIndicator = async () => {
    if (!editingIndicator?.name.trim()) return;
    setSavingIndicator(true);
    try {
      const response = await barrattAPI.updateIndicator(editingIndicator.id, {
        name: editingIndicator.name.trim(), chart_type: editingIndicator.chart_type,
        hint: editingIndicator.hint?.trim(), config: editingIndicator.config,
      });
      setIndicators(indicators.map(i => i.id === editingIndicator.id ? response.data : i));
      setEditingIndicator(null);
      setIsEditIndicatorOpen(false);
      toast.success('Indicator updated');
    } catch (error) { console.error('Failed to update indicator:', error); toast.error('Failed to update indicator'); }
    finally { setSavingIndicator(false); }
  };

  const handleDeleteIndicator = async () => {
    if (!deletingIndicator) return;
    try {
      await barrattAPI.deleteIndicator(deletingIndicator.id);
      setIndicators(indicators.filter(i => i.id !== deletingIndicator.id));
      setDeletingIndicator(null);
      setIsDeleteIndicatorOpen(false);
      toast.success('Indicator deleted');
    } catch (error) { console.error('Failed to delete indicator:', error); toast.error('Failed to delete indicator'); }
  };

  const openEditIndicator = (indicator: DashboardIndicator) => {
    setEditingIndicator({ ...indicator });
    setIsEditIndicatorOpen(true);
  };

  const openDeleteIndicator = (indicator: DashboardIndicator) => {
    setDeletingIndicator(indicator);
    setIsDeleteIndicatorOpen(true);
  };

  const dashboardsByCategory = useMemo(() => {
    const grouped: Record<string, AnalyseDashboard[]> = {};
    dashboards.forEach((dashboard) => {
      const categoryName = categories.find((c) => c.id === dashboard.category)?.name || 'Uncategorized';
      if (!grouped[categoryName]) grouped[categoryName] = [];
      grouped[categoryName].push(dashboard);
    });
    return grouped;
  }, [dashboards, categories]);

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analyse</h1>
          <p className="text-muted-foreground">Build and view dashboard indicators</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" size="sm"><Plus className="h-4 w-4 mr-2" />Create<ChevronDown className="h-4 w-4 ml-2" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsAddCategoryOpen(true)}><FolderPlus className="h-4 w-4 mr-2" />New Category</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsAddDashboardOpen(true)}><LayoutDashboard className="h-4 w-4 mr-2" />New Dashboard</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsAddIndicatorOpen(true)} disabled={!selectedDashboard}><BarChart3 className="h-4 w-4 mr-2" />Add Indicator</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {selectedDashboard && (<Button variant="outline" size="sm"><Download className="h-4 w-4 mr-2" />Export</Button>)}
        </div>
      </div>

      {/* Dashboard Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><LayoutDashboard className="h-5 w-5" />Select Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={selectedDashboard?.id.toString() || ''} onValueChange={handleDashboardChange}>
              <SelectTrigger className="w-[350px]"><SelectValue placeholder="Select a dashboard to view" /></SelectTrigger>
              <SelectContent>
                {Object.entries(dashboardsByCategory).map(([categoryName, categoryDashboards]) => (
                  <div key={categoryName}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">{categoryName}</div>
                    {categoryDashboards.map((dashboard) => (
                      <SelectItem key={dashboard.id} value={dashboard.id.toString()}>
                        <div className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />{dashboard.name}
                          {!dashboard.is_enabled && (<Badge variant="secondary" className="text-xs">Disabled</Badge>)}
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
                {dashboards.length === 0 && (<div className="px-2 py-4 text-center text-sm text-muted-foreground">No dashboards available. Create one to get started.</div>)}
              </SelectContent>
            </Select>
            {selectedDashboard && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Badge variant={selectedDashboard.is_enabled ? 'default' : 'secondary'}>{selectedDashboard.is_enabled ? 'Active' : 'Disabled'}</Badge>
                <span>|</span><span>{indicators.length} indicators</span>
              </div>
            )}
            {selectedDashboard && (<Button variant="outline" size="sm" onClick={() => setIsAddIndicatorOpen(true)}><Plus className="h-4 w-4 mr-2" />Add Indicator</Button>)}
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Content */}
      {selectedDashboard ? (
        <>
          {selectedDashboard.description && (
            <Card><CardContent className="py-3"><p className="text-sm text-muted-foreground">{selectedDashboard.description}</p></CardContent></Card>
          )}
          {loadingIndicators ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : indicators.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No indicators configured</h3>
                <p className="text-sm text-muted-foreground mb-4">Add charts and visualizations to this dashboard.</p>
                <Button onClick={() => setIsAddIndicatorOpen(true)}><Plus className="h-4 w-4 mr-2" />Add Indicator</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {indicators.map((indicator) => (
                <IndicatorCard key={indicator.id} indicator={indicator} onEdit={openEditIndicator} onDelete={openDeleteIndicator} />
              ))}
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <LayoutDashboard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">Select or Create a Dashboard</h3>
            <p className="text-sm text-muted-foreground mb-4">Choose a dashboard from the dropdown above or create a new one to get started.</p>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={() => setIsAddCategoryOpen(true)}><FolderPlus className="h-4 w-4 mr-2" />New Category</Button>
              <Button onClick={() => setIsAddDashboardOpen(true)}><Plus className="h-4 w-4 mr-2" />New Dashboard</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      {!selectedDashboard && (categories.length > 0 || dashboards.length > 0) && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Categories</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{categories.length}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Dashboards</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{dashboards.length}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Active Dashboards</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{dashboards.filter((d) => d.is_enabled).length}</div></CardContent></Card>
        </div>
      )}

      {/* Dialogs */}
      <CreateCategoryDialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen} newCategory={newCategory} setNewCategory={setNewCategory} saving={savingCategory} onCreate={handleCreateCategory} />
      <CreateDashboardDialog open={isAddDashboardOpen} onOpenChange={setIsAddDashboardOpen} newDashboard={newDashboard} setNewDashboard={setNewDashboard} categories={categories} saving={savingDashboard} onCreate={handleCreateDashboard} />
      <IndicatorDialog
        open={isAddIndicatorOpen} onOpenChange={setIsAddIndicatorOpen}
        title="Add Indicator" description={`Add a new chart or visualization to ${selectedDashboard?.name || 'the dashboard'}.`}
        indicator={newIndicator} setIndicator={(ind) => setNewIndicator({ ...ind, config: newIndicator.config })}
        saving={savingIndicator} onSave={handleCreateIndicator} saveLabel="Add Indicator"
      />
      {editingIndicator && (
        <IndicatorDialog
          open={isEditIndicatorOpen} onOpenChange={setIsEditIndicatorOpen}
          title="Edit Indicator" description="Update the indicator settings."
          indicator={{ name: editingIndicator.name, chart_type: editingIndicator.chart_type, hint: editingIndicator.hint || '' }}
          setIndicator={(ind) => setEditingIndicator({ ...editingIndicator, ...ind })}
          saving={savingIndicator} onSave={handleEditIndicator} saveLabel="Save Changes"
        />
      )}
      <DeleteIndicatorDialog
        open={isDeleteIndicatorOpen} onOpenChange={setIsDeleteIndicatorOpen}
        indicatorName={deletingIndicator?.name || ''} onDelete={handleDeleteIndicator}
        onCancel={() => setDeletingIndicator(null)}
      />
    </div>
  );
}
